import { StandardEvents } from '@shopify/events';

/**
 * CrafTech (10.09.2026): стълбица по количество (snippets/ct-qty-tiers.liquid).
 *
 * Таблицата е информативна: бройката се въвежда в полето (−/брой/+), редът на
 * достигнатото стъпало се маркира (aria-pressed), а сметката „N бр. × цена = общо“
 * се обновява живо от data-base/data-tiers (същият източник като автоматичните
 * отстъпки - метаполето custom.qty_tiers). Клик на ред попълва полето с минимума
 * на стъпалото. getValue() е за product-form.js (#getQuantity чете
 * refs.quantitySelector) - иначе опашката при смяна на вариант тръгва с 1.
 *
 * При смяна на вариант Horizon morph-ва .product-form-buttons само при смяна на
 * количествените правила, затова компонентът се пре-рендира сам и пази бройката.
 */
class CtQtyTiers extends HTMLElement {
  #target = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);
    // Същият списък като в ct-personalization.js: мобилният quick add маха обвивката
    // #ProductInformation-…, затова #quick-add-modal-content е тук (мерено 08.09.2026).
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], #quick-add-modal-content, product-card');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onInput);
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {number} Количеството, което ще замине към /cart/add. */
  getValue() {
    return Math.max(1, Math.floor(Number(this.querySelector('[data-ct-qty]')?.value)) || 1);
  }

  #setValue(qty) {
    const input = this.querySelector('[data-ct-qty]');
    if (input) input.value = String(Math.max(1, Math.floor(qty) || 1));
    this.#render();
  }

  #onClick = (event) => {
    const el = event.target instanceof Element ? event.target : null;
    const step = el?.closest('[data-ct-step]');
    if (step && this.contains(step)) {
      this.#setValue(this.getValue() + Number(step.dataset.ctStep));
      return;
    }
    const row = el?.closest('.ct-tiers__row');
    if (row && this.contains(row)) this.#setValue(Number(row.dataset.qty) || 1);
  };

  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.hasAttribute('data-ct-qty')) {
      if (event.type === 'change') this.#setValue(this.getValue());
      else this.#render();
    }
  };

  /** Стъпалата от data-tiers, сортирани по min. */
  #tiers() {
    try {
      return JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.min - b.min);
    } catch (e) {
      return [];
    }
  }

  /** Форматира евроцентове по образеца на магазина (data-money-sample = 123456 | money). */
  #money(cents) {
    const sample = this.dataset.moneySample || '€1.234,56';
    const m = sample.match(/1(\D?)234(\D)56/);
    const thousands = m ? m[1] : '';
    const decimal = m ? m[2] : ',';
    const abs = Math.abs(Math.round(cents));
    const whole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const frac = String(abs % 100).padStart(2, '0');
    const number = whole + decimal + frac;
    return m ? sample.replace(m[0], number) : number;
  }

  #render() {
    const qty = this.getValue();
    const base = Number(this.dataset.base) || 0;
    const tiers = this.#tiers();
    let reached = null;
    for (const t of tiers) if (qty >= t.min) reached = t;
    const off = reached ? Number(reached.off) : 0;
    const unit = Math.max(0, base - off);
    for (const r of this.querySelectorAll('.ct-tiers__row')) {
      const rowQty = Number(r.dataset.qty) || 1;
      const active = reached ? rowQty === reached.min : rowQty === 1;
      r.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    const totalEl = this.querySelector('[data-ct-total]');
    if (!totalEl) return;
    let text = (this.dataset.tTotal || '__Q__ × __U__ = __T__')
      .replace('__Q__', String(qty))
      .replace('__U__', this.#money(unit))
      .replace('__T__', this.#money(unit * qty));
    if (off > 0) text += ' · ' + (this.dataset.tSave || '__A__').replace('__A__', this.#money(off * qty));
    totalEl.textContent = text;
  }

  #onProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector('ct-qty-tiers');
        if (!next) return;
        const qty = this.getValue();
        this.dataset.base = next.dataset.base;
        this.dataset.tiers = next.dataset.tiers;
        this.innerHTML = next.innerHTML;
        this.#setValue(qty);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ct-qty-tiers] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ct-qty-tiers')) {
  customElements.define('ct-qty-tiers', CtQtyTiers);
}
